import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'

const ShipStationContext = createContext()

export function ShipStationProvider({ children }) {
  // Existing ship date functionality - KEEP INTACT
  const [shipByDates, setShipByDates] = useState({})
  const [loading, setLoading] = useState({})

  // NEW: Delivery date functionality
  const [deliveryDates, setDeliveryDates] = useState({})
  const [deliveryLoading, setDeliveryLoading] = useState({})

  // Keep refs in sync so callbacks can be stable without depending on state
  const shipByDatesRef = useRef(shipByDates)
  const loadingRef = useRef(loading)
  const deliveryDatesRef = useRef(deliveryDates)
  const deliveryLoadingRef = useRef(deliveryLoading)

  useEffect(() => { shipByDatesRef.current = shipByDates }, [shipByDates])
  useEffect(() => { loadingRef.current = loading }, [loading])
  useEffect(() => { deliveryDatesRef.current = deliveryDates }, [deliveryDates])
  useEffect(() => { deliveryLoadingRef.current = deliveryLoading }, [deliveryLoading])

  // Existing fetchShipByDate function - KEEP INTACT
  const fetchShipByDate = useCallback(async (orderNumber) => {
    const cleanOrderNumber = orderNumber?.replace('#', '')
    const currentShipByDates = shipByDatesRef.current
    const currentLoading = loadingRef.current

    if (!cleanOrderNumber || currentShipByDates[cleanOrderNumber] || currentLoading[cleanOrderNumber]) return

    setLoading((prev) => ({ ...prev, [cleanOrderNumber]: true }))
    try {
      const res = await fetch(`/api/shipstation?orderNumber=${cleanOrderNumber}`)
      if (!res.ok) return
      const data = await res.json()
      setShipByDates((prev) => ({
        ...prev,
        [cleanOrderNumber]: data.shipByDate,
      }))
    } finally {
      setLoading((prev) => ({ ...prev, [cleanOrderNumber]: false }))
    }
  }, [])

  // NEW: fetchDeliveryDate function
  const fetchDeliveryDate = useCallback(async (orderNumber) => {
    const cleanOrderNumber = orderNumber?.replace('#', '')
    const currentDeliveryDates = deliveryDatesRef.current
    const currentDeliveryLoading = deliveryLoadingRef.current

    if (!cleanOrderNumber || currentDeliveryDates[cleanOrderNumber] || currentDeliveryLoading[cleanOrderNumber]) return

    setDeliveryLoading((prev) => ({ ...prev, [cleanOrderNumber]: true }))
    try {
      const res = await fetch(`/api/shipstation/delivery?orderNumber=${cleanOrderNumber}`)
      if (!res.ok) throw new Error('Failed to fetch delivery date')
      const data = await res.json()

      // Store both the formatted date and success status
      setDeliveryDates((prev) => ({
        ...prev,
        [cleanOrderNumber]: {
          formattedDate: data.formattedDate,
          deliveryDate: data.deliveryDate,
          success: data.success,
          error: data.error
        },
      }))
    } catch (error) {
      setDeliveryDates((prev) => ({
        ...prev,
        [cleanOrderNumber]: {
          formattedDate: null,
          deliveryDate: null,
          success: false,
          error: 'Failed to fetch delivery date'
        },
      }))
    } finally {
      setDeliveryLoading((prev) => ({ ...prev, [cleanOrderNumber]: false }))
    }
  }, [])

  return (
    <ShipStationContext.Provider
      value={{ 
        // Existing functionality - KEEP INTACT
        shipByDates, 
        fetchShipByDate, 
        loading,
        // NEW: Delivery date functionality
        deliveryDates,
        fetchDeliveryDate,
        deliveryLoading
      }}
    >
      {children}
    </ShipStationContext.Provider>
  )
}

export function useShipStation() {
  return useContext(ShipStationContext)
}
